import { useGLTF, KeyboardControls } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { Physics } from '@react-three/rapier';
import { Suspense, useRef, useState, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { LoadingScreen } from './components/LoadingScreen';
import { PaperPlane } from './components/PaperPlane';
import { Ring } from './components/Ring';
import { Asterisc } from './components/Asterisc';

const GRAVITY_Y = -22;
const POINTS_ASTEROID = 4;
const POINTS_RING = 2;

const keyboardMap = [
  { name: 'lift', keys: ['Space', 'ArrowUp'] },
  { name: 'down', keys: ['ArrowDown'] },
];

// Afegit onGameOver a les props
export default function Experience({ setAppScore, onGameOver }) {
  const { camera } = useThree();
  const [planePosition, setPlanePosition] = useState(new THREE.Vector3(0, 0, 0));

  const [rings, setRings] = useState([]);
  const nextRingIdRef = useRef(0);
  const lastRingXRef = useRef(0);
  const [passedRingIds, setPassedRingIds] = useState(new Set());

  const hitAsteroidIdsRef = useRef(new Set());

  const [asteriscs, setAsteriscs] = useState([]);
  const nextAsteriscIdRef = useRef(0);

  const [isLoading, setIsLoading] = useState(true);

  const RING_SPACING = 15;
  const RINGS_AHEAD = 6;
  const MIN_Y_OBSTACLE = -2;
  const MAX_Y_OBSTACLE = 3;

  const generateRandomY = () => MIN_Y_OBSTACLE + Math.random() * (MAX_Y_OBSTACLE - MIN_Y_OBSTACLE);

  const generateNewAsterisc = (targetX) => {
    const y = generateRandomY();
    const id = nextAsteriscIdRef.current++;
    return { id, position: [targetX, y, 0.5] };
  };

  useEffect(() => {
    // Reiniciar estats interns quan el component es munta (per exemple, en reiniciar el joc)
    nextRingIdRef.current = 0;
    lastRingXRef.current = 0;
    setPassedRingIds(new Set());
    hitAsteroidIdsRef.current.clear();
    nextAsteriscIdRef.current = 0;
    setPlanePosition(new THREE.Vector3(0, 0, 0)); // Important reiniciar la posició de l'avió per la càmera

    const initialRings = [];
    const initialAsteriscs = [];
    let currentSpawnX = 15;

    for (let i = 0; i < RINGS_AHEAD; i++) {
      initialRings.push({
        id: nextRingIdRef.current++,
        position: [currentSpawnX, generateRandomY(), 0],
      });

      if (Math.random() < 0.6) {
        const asteriscXOffset = (Math.random() - 0.5) * (RING_SPACING * 0.8);
        initialAsteriscs.push(generateNewAsterisc(currentSpawnX + asteriscXOffset));
      }
      currentSpawnX += RING_SPACING;
    }
  
    setRings(initialRings);
    setAsteriscs(initialAsteriscs);

    if (initialRings.length > 0) {
      lastRingXRef.current = initialRings[initialRings.length - 1].position[0];
    } else {
      lastRingXRef.current = 5; 
    }
  }, []); // Aquest useEffect es crida quan Experience es munta.

  const removeRing = useCallback((ringId) => {
    setRings(currentRings => currentRings.filter(ring => ring.id !== ringId));
  }, []);

  const removeAsterisc = useCallback((asteriscId) => {
    setAsteriscs(currentAsteriscs => {
      return currentAsteriscs.filter(a => a.id !== asteriscId);
    });
  }, []);

  const handleRingPass = useCallback((ringId) => {
    if (!passedRingIds.has(ringId)) {
      setAppScore(prevScore => {
        const newScore = prevScore + POINTS_RING;
        return newScore;
      });
      setPassedRingIds(prev => new Set(prev).add(ringId));
    }
  }, [setAppScore, passedRingIds]);

  const handleAsteriscCollision = useCallback((asteriscId) => {
    if (hitAsteroidIdsRef.current.has(asteriscId)) {
      return;
    }
    setAppScore(prevScore => {
      const newScore = prevScore + POINTS_ASTEROID;
      return newScore;
    });
    hitAsteroidIdsRef.current.add(asteriscId);
    removeAsterisc(asteriscId);
  }, [setAppScore, removeAsterisc]);

  useFrame((state, delta) => {
    if (isLoading) return;

    const planeX = planePosition.x;
    
    if (lastRingXRef.current - planeX < RINGS_AHEAD * RING_SPACING * 0.7) {
      const newRingX = lastRingXRef.current + RING_SPACING;
      
      setRings(currentRings => [
        ...currentRings,
        {
          id: nextRingIdRef.current++,
          position: [newRingX, generateRandomY(), 0]
        }
      ]);
      lastRingXRef.current = newRingX;

      if (Math.random() < 0.7) {
        const asteriscSpawnX = newRingX - (RING_SPACING / 2) + (Math.random() - 0.5) * (RING_SPACING * 0.5);
        setAsteriscs(currentAsteriscs => [
            ...currentAsteriscs,
            generateNewAsterisc(asteriscSpawnX)
        ]);
      }
    }

    const targetCameraPosition = new THREE.Vector3(
      planePosition.x - 7,
      planePosition.y + 2,
      5
    );
    
    camera.position.lerp(targetCameraPosition, Math.min(delta * 1.5, 0.05)); 
    camera.lookAt(planePosition.x + 4, planePosition.y, 0);
  });

  const handlePlaneReady = () => {
    setIsLoading(false);
  };

  return (
    <> 
      <KeyboardControls map={keyboardMap}>
        {isLoading && <LoadingScreen />}
        
        <ambientLight intensity={4} /> 
        <directionalLight 
          position={[5, 10, 2]}
          intensity={2.0} 
        />

        <Suspense fallback={null}>
          <Physics gravity={[0, GRAVITY_Y, 0]} /*debug={true}*/>
            <PaperPlane 
              onPositionUpdate={setPlanePosition} 
              onReady={handlePlaneReady}
              onRingSensorIntersect={handleRingPass}
              onGameOver={onGameOver} // Passar la funció de game over
            />
            
            {rings.map((ring) => (
              <Ring 
                key={`ring-${ring.id}`}
                id={ring.id}
                position={ring.position} 
                onRemove={removeRing}
              />
            ))}

            {asteriscs.map((asterisc) => (
              <Asterisc
                key={`asterisc-${asterisc.id}`}
                id={asterisc.id}
                position={asterisc.position}
                onCollide={handleAsteriscCollision}
                onRemove={removeAsterisc}
              />
            ))}
          </Physics>
        </Suspense>
      </KeyboardControls>
    </>
  );
}

useGLTF.preload('./plen.glb', './draco/');
useGLTF.preload('./asterisc.glb', './draco/');